import type { Action, ActionIntent } from './Action';
import { showApiErrorToast } from '@packages/shared/utils';

export interface ActionExecutionContext {
  pk: string;
  row: Record<string, unknown>;
  selectedKeys?: string[];
  /** Full row objects for the current selection (resolved from table data). */
  selectedRows?: Record<string, unknown>[];
  tableQuery?: Record<string, unknown>;
  refreshTable: () => void;
  /** Clears row selection and resets select-all-pages state after bulk actions. */
  clearSelection?: () => void;
  /** True when "Select All Pages" is active (filter-based bulk endpoint). */
  selectAllActive?: boolean;
  /** IDs explicitly unchecked after Select All Pages was activated. */
  deselectedRowIds?: string[];
  /** Server-side total count for the current query/filter. */
  totalCount?: number;
  /** Configured axios instance with auth headers and base URL. */
  axiosInstance?: { get: Function; post: Function; put: Function; delete: Function; request: Function };
  /** File selected via toolbar import input (automation-compatible hidden file input). */
  file?: File;
  /** Parameters passed to data actions (e.g., resolved tokens like entityId). */
  params?: Record<string, string>;
}

export interface ActionableResult {
  visible: boolean;
  enabled: boolean;
  reason?: string;
}

export type PermissionResolver = (perm: { action: string; resource: string }) => boolean;

type NavigateFunction = (to: string, options?: { state?: unknown }) => void;
type IntentListener = (intent: ActionIntent) => void;
type DomainLoader = () => Promise<unknown>;

const PERMISSION_RESOLVER_KEY = '__WEALTHLANE_PERMISSION_RESOLVER__';
const ACTION_OBJECTS_KEY = '__WEALTHLANE_ACTION_OBJECTS__';
const NAVIGATE_KEY = '__WEALTHLANE_NAVIGATE__';
const INTENT_LISTENERS_KEY = '__WEALTHLANE_INTENT_LISTENERS__';
const DOMAIN_LOADERS_KEY = '__WEALTHLANE_DOMAIN_LOADERS__';
const LOADED_DOMAINS_KEY = '__WEALTHLANE_LOADED_DOMAINS__';
const LOADING_PROMISES_KEY = '__WEALTHLANE_LOADING_PROMISES__';

function getActionObjects(): Map<string, Action> {
  const w = window as any;
  if (!w[ACTION_OBJECTS_KEY]) {
    w[ACTION_OBJECTS_KEY] = new Map<string, Action>();
  }
  return w[ACTION_OBJECTS_KEY] as Map<string, Action>;
}

function getIntentListeners(): Set<IntentListener> {
  const w = window as any;
  if (!w[INTENT_LISTENERS_KEY]) {
    w[INTENT_LISTENERS_KEY] = new Set<IntentListener>();
  }
  return w[INTENT_LISTENERS_KEY] as Set<IntentListener>;
}

function getDomainLoaders(): Record<string, DomainLoader> {
  const w = window as any;
  if (!w[DOMAIN_LOADERS_KEY]) {
    w[DOMAIN_LOADERS_KEY] = {};
  }
  return w[DOMAIN_LOADERS_KEY] as Record<string, DomainLoader>;
}

function getLoadedDomains(): Set<string> {
  const w = window as any;
  if (!w[LOADED_DOMAINS_KEY]) {
    w[LOADED_DOMAINS_KEY] = new Set<string>();
  }
  return w[LOADED_DOMAINS_KEY] as Set<string>;
}

function getLoadingPromises(): Map<string, Promise<unknown>> {
  const w = window as any;
  if (!w[LOADING_PROMISES_KEY]) {
    w[LOADING_PROMISES_KEY] = new Map<string, Promise<unknown>>();
  }
  return w[LOADING_PROMISES_KEY] as Map<string, Promise<unknown>>;
}

class ActionRegistryImpl {
  // ─── Action-object API (OOP pattern — self-registering modules) ──

  /**
   * Register an Action object by its semantic ID.
   * Called at module top-level in self-registering `.actions.ts` files.
   */
  registerAction(action: Action): void {
    const objects = getActionObjects();
    if (objects.has(action.id)) return;
    objects.set(action.id, action);
  }

  /** Resolve an Action object by ID. Returns null if not found. */
  resolveAction(actionId: string): Action | null {
    return getActionObjects().get(actionId) ?? null;
  }

  /** Check if an Action object is registered for this ID. */
  hasAction(actionId: string): boolean {
    return getActionObjects().has(actionId);
  }

  /** Delegate to Action.isAvailable(). Returns false if action not found. */
  isActionAvailable(actionId: string): boolean {
    const action = getActionObjects().get(actionId);
    if (!action) return false;
    return action.isAvailable();
  }

  /** Delegate to Action.isActionable(row). */
  isActionActionable(actionId: string, row: Record<string, unknown>): ActionableResult {
    const action = getActionObjects().get(actionId);
    if (!action) return { visible: false, enabled: false };
    return action.isActionable(row);
  }

  /**
   * Delegate to Action.execute(ctx).
   *
   * Global safety net: if the action throws an unhandled error, it is caught
   * here and routed to `showApiErrorToast` before re-throwing. This ensures
   * users always see feedback for API failures — even when the action author
   * forgets explicit error handling.
   *
   * Actions that already handle errors (via `confirm({ onAction })`,
   * `notify.promise`, or `safeApiCall`) swallow internally and never re-throw,
   * so this catch does NOT produce double toasts — it only fires for truly
   * unhandled errors.
   *
   * Re-throwing preserves existing caller behavior (e.g., FileListNode catch
   * sets download error state, ActionButtonNode finally stops spinner).
   */
  async executeAction(actionId: string, ctx: ActionExecutionContext): Promise<void> {
    const action = getActionObjects().get(actionId);
    if (!action) {
      console.warn(`[ActionRegistry] No Action object for "${actionId}"`);
      return;
    }
    try {
      await action.execute(ctx);
    } catch (error: unknown) {
      showApiErrorToast(error);
      throw error;
    }
  }

  // ─── Data actions (view engine integration) ────────────────────

  /**
   * Execute a data action and return its result.
   * Used by the view engine's DataNode to fetch data for a view schema.
   * Constructs a minimal context with only `params` populated.
   */
  async executeData(actionId: string, params: Record<string, string>): Promise<Record<string, unknown>> {
    const action = getActionObjects().get(actionId);
    if (!action) {
      throw new Error(`[ActionRegistry] No Action object for data action "${actionId}"`);
    }
    const ctx: ActionExecutionContext = {
      pk: '',
      row: {},
      refreshTable: () => {},
      params,
    };
    const result = await action.execute(ctx);
    return (result as Record<string, unknown>) ?? {};
  }

  // ─── Domain loaders (convention-based auto-loading) ─────────────

  /** Extract the domain from an action ID. Last word, lowercased. */
  getDomain(actionId: string): string {
    const parts = actionId.toLowerCase().split('_');
    return parts[parts.length - 1];
  }

  /** Register a domain loader. One line per domain. */
  registerDomainLoader(domain: string, loader: DomainLoader): void {
    getDomainLoaders()[domain] = loader;
  }

  /**
   * Ensure all actions for the given IDs are loaded.
   * Called by DynamicTableV2 during schema init.
   * Derives domain from each ID, loads unloaded domains in parallel.
   */
  async ensureActions(actionIds: string[]): Promise<void> {
    const domainsToLoad = new Set<string>();
    const loadedDomains = getLoadedDomains();
    const loaders = getDomainLoaders();

    for (const id of actionIds) {
      if (getActionObjects().has(id)) continue;
      const domain = this.getDomain(id);
      if (!loadedDomains.has(domain) && loaders[domain]) {
        domainsToLoad.add(domain);
      }
    }

    if (domainsToLoad.size === 0) return;

    const loadingPromises = getLoadingPromises();

    await Promise.all(
      [...domainsToLoad].map((domain) => {
        loadedDomains.add(domain);
        if (!loadingPromises.has(domain)) {
          const promise = loaders[domain]();
          loadingPromises.set(domain, promise);
        }
        return loadingPromises.get(domain)!;
      }),
    );
  }

  // ─── Navigation (set once by AppShell) ──────────────────────────

  setNavigate(fn: NavigateFunction): void {
    (window as any)[NAVIGATE_KEY] = fn;
  }

  getNavigate(): NavigateFunction {
    const fn = (window as any)[NAVIGATE_KEY];
    if (!fn) {
      console.warn('[ActionRegistry] navigate not set. Call ActionRegistry.setNavigate() in AppShell.');
      return () => {};
    }
    return fn as NavigateFunction;
  }

  // ─── Intent system (Mediator Pattern for modal actions) ─────────

  emitIntent(intent: ActionIntent): void {
    const listeners = getIntentListeners();
    for (const listener of listeners) {
      listener(intent);
    }
  }

  /** Subscribe to intents. Returns an unsubscribe function. */
  onIntent(listener: IntentListener): () => void {
    const listeners = getIntentListeners();
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }

  // ─── Permission resolver (shared) ──────────────────────────────

  setPermissionResolver(resolver: PermissionResolver): void {
    (window as any)[PERMISSION_RESOLVER_KEY] = resolver;
  }

  getPermissionResolver(): PermissionResolver | null {
    return (window as any)[PERMISSION_RESOLVER_KEY] ?? null;
  }

  clear(): void {
    getActionObjects().clear();
    getIntentListeners().clear();
    getLoadedDomains().clear();
    getLoadingPromises().clear();
  }

  __debugKeys(): string[] {
    return Array.from(getActionObjects().keys()).map((k) => `[Action] ${k}`);
  }
}

export const ActionRegistry = new ActionRegistryImpl();
